import React, { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'
import BkScreen from './BkScreen'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import BkPixel from './BkPixel'
import { stitchText } from './pixelType'
import { BK_ASSET, BK_HIT, bkHit } from './layout'
import { forgetBkUploads, whenBkGalleryReady } from './mediaUpload'

/**
 * Screen 10 — "scan the QR code to obtain photos".
 *
 * The gallery lives on the STUDIO host, never on the booth's own origin, which
 * is what `resolveGallerySessionUrlFromStore` settles: same-host proxying
 * answers with a relative redirect the browser resolves back against the booth,
 * and the guest gets the app shell instead of their photographs.
 *
 * The QR waits for the gallery. Shown at once, it was scanned within seconds
 * (blh6kn, 2026-10-02: 4.5 s), before the photographs and strip had landed,
 * and the gallery page answered "No images found" and never looked again.
 * Until the uploads settle the white box shows three dots in the plates' block
 * lettering, and the page's timeout starts with the QR, so the guest keeps the
 * full time to scan.
 */
const Result: React.FC = () => {
  const sessionId = useSessionStateStore((s) => s.id)
  const { setSessionState, logActivity } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { config } = useConfigStore()
  const [qr, setQr] = useState<string | null>(null)
  const [galleryReady, setGalleryReady] = useState(false)
  const finishedRef = useRef(false)

  /**
   * End the session the way stock Result does: stamp its end and length, mark
   * it completed, then finalize — a forced upload plus a sync, which also
   * pushes any photo the booth is still holding (offline, or a slow upload
   * handed to the background queue) — and hand over to sessionCleanup, which
   * returns the booth to Welcome and clears it for the next guest.
   *
   * The upload is handed a snapshot taken here, not left to read the store:
   * sessionCleanup resets the store while the upload is still on its way, and
   * the guest's session must not be posted half-cleared.
   */
  const finish = useCallback(
    (reason: 'timeout' | 'button'): void => {
      if (finishedRef.current) return
      finishedRef.current = true
      logActivity(`BK session finished (${reason})`)
      const startTime = useSessionStateStore.getState().timeline?.startTime
      const endTime = new Date()
      setSessionState({
        timeline: {
          endTime,
          duration: startTime ? endTime.getTime() - new Date(startTime).getTime() : null,
          isCompleted: true
        }
      })
      if (sessionId) {
        const snapshot = { ...useSessionStateStore.getState() } as unknown as Record<string, unknown>
        stateUploader
          .uploadSessionState(sessionId, snapshot, { force: true })
          .then(() => stateUploader.triggerSyncNow())
          .catch((err: unknown) => console.warn('[BK] final session upload failed:', err))
      }
      if (sessionId) forgetBkUploads(sessionId)
      sessionCleanup('session_completed')
    },
    [logActivity, sessionCleanup, sessionId, setSessionState]
  )

  const remaining = useBkPageTimeout(
    config?.pages?.result?.settings?.resultTimeout,
    () => finish('timeout'),
    galleryReady
  )

  useEffect(() => {
    if (!sessionId) return
    let current = true
    void whenBkGalleryReady(sessionId).then(({ waitedMs, capped }) => {
      if (!current) return
      useSessionStateStore
        .getState()
        .logActivity(
          capped
            ? 'BK gallery QR shown at the wait cap, uploads still running'
            : 'BK gallery QR shown once the uploads settled',
          { waitedMs, level: capped ? 'warning' : 'info' }
        )
      setGalleryReady(true)
    })
    return (): void => {
      current = false
    }
  }, [sessionId])

  useEffect(() => {
    if (!sessionId) return
    const url = resolveGallerySessionUrlFromStore(sessionId)
    QRCode.toDataURL(url, { margin: 0, width: 420 })
      .then(setQr)
      .catch((error) => console.error('[BK] gallery QR failed:', error))
  }, [sessionId])

  return (
    <BkScreen plate="gallery.webp">
      <div
        style={bkHit(BK_HIT.result.qr)}
        className="flex items-center justify-center bg-white p-3"
      >
        {qr && galleryReady ? (
          <img src={qr} alt="Gallery QR code" className="h-full w-full" />
        ) : (
          <BkPixel rows={stitchText('...')} block={10} className="animate-pulse" />
        )}
      </div>
      <button
        aria-label="Done"
        onClick={() => finish('button')}
        style={bkHit(BK_HIT.result.done)}
        className="cursor-pointer bg-transparent p-0"
      >
        <img src={BK_ASSET('done-swan.webp')} alt="" aria-hidden className="h-full w-full" />
      </button>
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Result
